'use client'

import type { ReactNode } from 'react'

import { BaseCarousel } from '@/components/common/carousel/BaseCarousel'
import { useDashboardBids } from '@/components/ordersBidsDashboard/useDashboardBids'
import { deriveLosingBidCategory } from '@/helpers/betterOdds/deriveLosingBidCategory'
import { useBetterOddsCandidates } from '@/queries/betterOddsCandidatesQuery'

import BetterOddsCarousel from './BetterOddsCarousel'

const CAROUSEL_CANDIDATE_LIMIT = 10

/**
 * Client-side swap over the server-resolved lead carousel: shows the
 * "Better Odds" carousel when the buyer has a losing/lost bid and there are
 * gap-eligible listings in that category, otherwise renders `fallback`
 * (whatever resolveHomeLeadCarousel picked) unchanged. Shows a loading
 * skeleton while resolving eligibility rather than flashing `fallback`
 * first — its data source (useDashboardBids) is the same query the
 * dashboard card beside it uses, so both settle together instead of the
 * carousel visibly swapping a moment after the page loads.
 *
 * Deliberately a client-side swap rather than a new entry in
 * resolveHomeLeadCarousel's server-side chain: that chain's other entries
 * are prefetched server components, and giving this one the same treatment
 * would mean a new authenticated server-side bid fetch. This reuses
 * useDashboardBids (already a client hook, already shared with the
 * in-development Orders/Bids dashboard's cache key) instead.
 */
export default function BetterOddsCarouselSlot({
  fallback,
}: {
  fallback: ReactNode
}) {
  const { data, isLoading } = useDashboardBids()

  const { category, excludeListingIds } = deriveLosingBidCategory(data)

  const { candidates, isLoading: isCandidatesLoading } =
    useBetterOddsCandidates({
      category,
      excludeListingIds,
      limit: CAROUSEL_CANDIDATE_LIMIT,
    })

  // The candidates query is disabled (never fetches) when there's no
  // category, so its isLoading can stay true forever in that case — only
  // wait on it once we actually have a category to search.
  const isResolvingEligibility =
    isLoading || (!!category && isCandidatesLoading)

  if (isResolvingEligibility) {
    // Blank title: we don't yet know if this ends up Better Odds or
    // `fallback`, so a skeleton with no title beats guessing wrong and
    // having it change a moment later. Renders in place of `fallback`
    // (not before it) so there's one reveal, not a swap.
    return (
      <BaseCarousel
        data={null}
        isLoading
        title=""
        carouselName="better_odds"
        enableGTMTracking={false}
      />
    )
  }

  if (!category || candidates.length === 0) {
    return fallback
  }

  return (
    <BetterOddsCarousel
      candidates={candidates}
      category={category}
      isLoading={false}
    />
  )
}
